import { useCallback, useEffect, useState } from 'react';
import { listProjectArtifacts, type RegistryArtifact } from '../services/artifactRegistry';
import {
  buildVersionChains,
  chainsToWorkspaceArtifacts,
  type ArtifactVersionChain,
} from '../utils/artifactVersions';
import type { WorkspaceArtifact } from '../utils/workspaceViewModel';

interface UseProjectArtifactsResult {
  /** Version chains, newest activity first. Empty when there is no project. */
  chains: ArtifactVersionChain[];
  /** Latest version of each chain, in the shape the workspace renders. */
  artifacts: WorkspaceArtifact[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Read the backend artifact registry for a project.
 *
 * Artifacts are project-scoped, so this returns nothing for a project-less
 * session and the caller keeps its message-derived view. Failures are reported
 * rather than thrown: the registry going down must not blank the workspace.
 */
export function useProjectArtifacts(
  projectId: string | undefined,
  sessionId: string | undefined,
  /** Bump to refetch — e.g. when a generation finishes. */
  revision: number = 0,
): UseProjectArtifactsResult {
  const scope = projectId && sessionId ? JSON.stringify([projectId, sessionId]) : undefined;
  const [snapshot, setSnapshot] = useState<{ scope?: string; records: RegistryArtifact[]; loading: boolean; error: string | null }>({ records: [], loading: false, error: null });
  const [manualRevision, setManualRevision] = useState(0);

  const refresh = useCallback(() => setManualRevision(value => value + 1), []);

  useEffect(() => {
    if (!projectId || !sessionId) {
      setSnapshot({ records: [], loading: false, error: null });
      return;
    }

    let cancelled = false;
    setSnapshot(previous => ({ scope, records: previous.scope === scope ? previous.records : [], loading: true, error: null }));

    listProjectArtifacts(projectId, { sessionId })
      .then(result => {
        if (cancelled) return;
        setSnapshot({ scope, records: result.filter(record => record.projectId === projectId && record.sessionId === sessionId), loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSnapshot({ scope, records: [], loading: false, error: err instanceof Error ? err.message : 'Failed to load artifacts' });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, sessionId, scope, revision, manualRevision]);

  // An effect runs after rendering. A newly selected conversation must never render the old conversation's
  // records even for that first frame, and a draft with no session id must not list every artifact of its project.
  const current = snapshot.scope === scope ? snapshot : { records: [], loading: Boolean(scope), error: null };
  const chains = buildVersionChains(current.records);

  return {
    chains,
    artifacts: chainsToWorkspaceArtifacts(chains),
    loading: current.loading,
    error: current.error,
    refresh,
  };
}
